<template>
  <div class="railway-calculator-edit">
    <div class="editor-header">
      <div class="header-title">
        <h2>
          <i class="bx bx-train"></i>
          Редактор калькулятора железнодорожных перевозок
        </h2>
        <div v-if="updatedAt" class="last-updated">
          <i class="bx bx-time"></i>
          Последнее обновление: {{ formatDate(updatedAt) }}
        </div>
      </div>
      <div class="editor-actions">
        <button 
          class="btn btn-success"
          @click="addFclTariff"
        >
          <i class="bx bx-plus"></i>
          Добавить FCL тариф
        </button>
        <button 
          class="btn btn-secondary"
          @click="$emit('close-editor')"
        >
          <i class="bx bx-x"></i>
          Закрыть редактор
        </button>
        <button 
          class="btn btn-primary"
          @click="saveCalculator"
          :disabled="isSaving"
        >
          <i class="bx bx-save"></i>
          {{ isSaving ? 'Сохранение...' : 'Сохранить' }}
        </button>
      </div>
    </div>
    
    <div class="editor-content">
      <!-- FCL калькулятор -->
      <div class="calculator-section">
        <h3>
          <i class="bx bx-package"></i>
          FCL (Full Container Load) - Контейнерные перевозки
        </h3>
        
        <div class="fcl-table-container">
          <!-- Сообщение когда нет тарифов -->
          <div v-if="fclTariffs.length === 0" class="empty-tariffs-message">
            <i class="bx bx-info-circle"></i>
            <p>Пока нет данных о FCL тарифах</p>
          </div>
          
          <!-- Таблица FCL тарифов -->
          <table v-else class="fcl-table">
            <thead>
              <tr>
                <th>Terms</th>
                <th>Город отправления</th>
                <th>POD (Пункт назначения)</th>
                <th>40HQ/COC/USD</th>
                <th style="text-align: center;">Действия</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(tariff, index) in fclTariffs" :key="index">
                <td>
                  <input 
                    v-model="tariff.terms" 
                    type="text" 
                    class="form-control"
                    placeholder="FOB"
                  />
                </td>
                <td>
                  <input 
                    v-model="tariff.origin_city" 
                    type="text" 
                    class="form-control"
                    placeholder="Шанхай / Shanghai"
                  />
                </td>
                <td>
                  <input 
                    v-model="tariff.pod" 
                    type="text" 
                    class="form-control"
                    placeholder="Москва"
                  />
                </td>
                <td>
                  <input 
                    v-model.number="tariff.cost_40hq" 
                    type="number" 
                    class="form-control"
                    step="0.01"
                    min="0"
                    placeholder="1000"
                  />
                </td>
                <td style="text-align: center;">
                  <button 
                    v-if="!tariff.is_default"
                    class="btn btn-danger btn-sm"
                    @click="removeFclTariff(index)"
                  >
                    <i class="bx bx-trash"></i>
                  </button>
                  <span v-else class="text-muted">Системный</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      
      <!-- LCL калькулятор -->
      <div class="calculator-section">
        <h3>
          <i class="bx bx-box"></i>
          LCL (Less than Container Load) - Сборные грузы
        </h3>
        
        <div class="lcl-settings">
          <div class="lcl-item">
            <label>Changzhou-Xi'an (EXPRESS)</label>
            <input 
              v-model.number="lclSettings.express_cost" 
              type="number" 
              class="form-control"
              step="0.01"
              min="0"
            />
          </div>
          <div class="lcl-item">
            <label>Handling Charges</label>
            <input 
              v-model.number="lclSettings.handling_charges" 
              type="number" 
              class="form-control"
              step="0.01"
              min="0"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { API_CALCULATOR_SETTINGS_RAILWAY } from '@/constants'

export default {
  name: 'RailwayCalculatorEdit',
  
  data() {
    return {
      isSaving: false,
      isLoading: true,
      fclTariffs: [],
      lclSettings: {
        express_cost: 0,
        handling_charges: 0
      },
      updatedAt: null
    }
  },
  
  async mounted() {
    await this.loadSettings()
  },
  
  methods: {
    async loadSettings() {
      try {
        this.isLoading = true
        const response = await axios.get(API_CALCULATOR_SETTINGS_RAILWAY)
        
        if (response.data.success && response.data.data) {
          this.fclTariffs = response.data.data.fcl_tariffs || []
          this.lclSettings = response.data.data.lcl_settings || {
            express_cost: 0,
            handling_charges: 0
          }
          this.updatedAt = response.data.updated_at
        } else {
          this.fclTariffs = []
          this.lclSettings = {
            express_cost: 0,
            handling_charges: 0
          }
          this.updatedAt = null
        }
      } catch (error) {
        console.error('Ошибка загрузки настроек:', error)
        this.fclTariffs = []
        this.lclSettings = {
          express_cost: 0,
          handling_charges: 0
        }
        this.$toast.error('Ошибка загрузки настроек калькулятора')
      } finally {
        this.isLoading = false
      }
    },
    
    addFclTariff() {
      this.fclTariffs.push({
        terms: 'FOB',
        origin_city: '',
        pod: '',
        cost_40hq: 0,
        is_default: false
      })
    },
    
    removeFclTariff(index) {
      this.fclTariffs.splice(index, 1)
    },
    
    async saveCalculator() {
      try {
        this.isSaving = true
        
        const settings = {
          fcl_tariffs: this.fclTariffs,
          lcl_settings: this.lclSettings
        }
        
        const response = await api.call('put', API_CALCULATOR_SETTINGS_RAILWAY, { settings })
        
        if (response.data.success) {
          this.updatedAt = response.data.updated_at
          this.$toast.success('Калькулятор ЖД перевозок успешно сохранен')
        } else {
          this.$toast.error(response.data.message || 'Ошибка сохранения')
        }
      } catch (error) {
        console.error('Ошибка сохранения:', error)
        this.$toast.error('Ошибка сохранения настроек калькулятора')
      } finally {
        this.isSaving = false
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString('ru-RU', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.railway-calculator-edit {
  overflow: hidden;
  
  .editor-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 15px 0px 30px 0;
    
    .header-title {
      display: flex;
      flex-direction: column;
      gap: 8px;
      
      h2 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        font-size: 20px;
        font-weight: 600;
        color: #0d6348;
        
        i {
          font-size: 24px;
        }
      }
      
      .last-updated {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        color: #6c757d;
        font-style: italic;
        
        i {
          font-size: 14px;
        }
      }
    }
    
    .editor-actions {
      display: flex;
      gap: 8px;
      
      .btn {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 6px 12px;
        border: none;
        border-radius: 4px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;
        font-size: 12px;
        
        i {
          font-size: 14px;
        }
        
        &.btn-primary {
          background: #e37926;
          color: white;
          border: 1px solid #d16a1a;
          
          &:hover:not(:disabled) {
            background: #d16a1a;
          }
          
          &:disabled {
            background: rgba(227, 121, 38, 0.6);
            cursor: not-allowed;
          }
        }
        
        &.btn-secondary {
          background: transparent;
          color: #0d6348;
          border: 1px solid #0d6348;
          
          &:hover {
            background: #0d6348;
            color: white;
          }
        }
      }
    }
  }
  
  .editor-content {
    padding: 20px;
    
    .calculator-section {
      margin-bottom: 30px;
      
      h3 {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0 0 15px 0;
        color: #0d6348;
        font-size: 18px;
        font-weight: 600;
        
        i {
          font-size: 20px;
        }
      }
      
      .fcl-table-container {
        overflow-x: auto;
        margin-bottom: 20px;
        border-radius: 6px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        
        .empty-tariffs-message {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px 20px;
          background: #f8f9fa;
          border-radius: 6px;
          color: #6c757d;
          
          i {
            font-size: 36px;
            margin-bottom: 12px;
            color: #0d6348;
          }
          
          p {
            margin: 0;
            font-size: 14px;
            font-weight: 500;
          }
        }
        
        .fcl-table {
          width: 100%;
          border-collapse: collapse;
          background: #fff;
          min-width: 600px;
          
          th {
            background: #0d6348;
            color: white;
            font-weight: 600;
            padding: 8px 6px;
            text-align: left;
            border: 1px solid #0a4d3a;
            font-size: 11px;
            white-space: nowrap;
          }
          
          td {
            padding: 4px 6px;
            border: 1px solid #dee2e6;
            vertical-align: middle;
            
            .form-control {
              width: 100%;
              padding: 4px 6px;
              border: 1px solid #ced4da;
              border-radius: 3px;
              font-size: 11px;
              
              &:focus {
                outline: none;
                border-color: #0d6348;
                box-shadow: 0 0 0 2px rgba(13, 99, 72, 0.25);
              }
            }
            
            .btn {
              padding: 3px 6px;
              font-size: 11px;
              border-radius: 3px;
              
              &.btn-danger {
                background: #dc3545;
                color: white;
                border: none;
                
                &:hover {
                  background: #c82333;
                }
              }
            }
            
            .text-muted {
              color: #6c757d;
              font-size: 12px;
              font-style: italic;
            }
          }
        }
      }
      
      .lcl-settings {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 15px;
        
        .lcl-item {
          background: #f8f9fa;
          padding: 15px;
          border-radius: 6px;
          border: 1px solid #e9ecef;
          
          label {
            display: block;
            margin-bottom: 8px;
            font-weight: 600;
            color: #0d6348;
            font-size: 12px;
          }
          
          .form-control {
            width: 100%;
            padding: 6px 8px;
            border: 1px solid #ced4da;
            border-radius: 4px;
            font-size: 12px;
            
            &:focus {
              outline: none;
              border-color: #0d6348;
              box-shadow: 0 0 0 2px rgba(13, 99, 72, 0.25);
            }
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .railway-calculator-edit {
    .editor-header {
      flex-direction: column;
      gap: 15px;
      text-align: center;
      
      .editor-actions {
        width: 100%;
        justify-content: center;
      }
    }
  }
}
</style>
